import React, { memo, useCallback, useState, useMemo, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';
import { graphql } from 'react-relay';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import { CommentEdited } from '@atlaskit/comment/edited';
import type {
	ExtensionHandler,
	ExtensionHandlers,
} from '@atlaskit/editor-common/extensions/extension-handler';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { Card } from '@atlaskit/smart-card/card/lazy';

import { ConfluenceSiteAri } from '@atlassian/ari/confluence/site';
import {
	AiBrand,
	KnowledgeDiscoveryKeyPhraseInputTextFormat,
	parseContent,
	useAutohighlightSupplier,
	useDefinitionSupplier,
	type DefinitionExperience,
} from '@atlassian/search-ai';
import { RendererDefinitionTextHighlighter } from '@atlassian/search-ai/v2';

import { fg } from '@confluence/feature-gating';
import { useEventHandlers } from '@confluence/adf-renderer/entry-points/useEventHandlers.ts';
import { createLinkEventHandler } from '@confluence/adf-renderer/entry-points/createLinkEventHandler.ts';
import { createSmartCardEventHandler } from '@confluence/adf-renderer/entry-points/createSmartCardEventHandler.ts';
import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { FALLBACK_REASONS } from '@confluence/comment-extension-handlers/entry-points/commentExtensionConstants.ts';
import { getExtensionHandlers } from '@confluence/comment-extension-handlers/entry-points/comment-macro-extension-handler.ts';
import { getConfluenceContentAri } from '@confluence/content-types-utils/entry-points/getConfluenceContentAri.ts';
import {
	DynamicAnalyticsListener,
	editorChannel,
} from '@confluence/unique-page-load-id/entry-points/DynamicAnalyticsListener.ts';
import {
	useRouteActions,
	useMatchRoute,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { TimeAgo } from '@confluence/time-ago';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { MultiMacroQueryContainer } from '@confluence/fabric-extension-queries/entry-points/multiMacroQuery.ts';
import { useDefinitionRestrictionOptions } from '@confluence/contextual-reading-aids/entry-points/useDefinitionRestrictionOptions.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import { CommentRendererLocation } from './commentTypes.ts';

const NARROW_EMBED_THRESHOLD = 480;

const nativeEmbedWrapperStyle = css({ width: '100%' });

const NativeEmbedCard = ({ url }: { url: string }) => {
	const [appearance, setAppearance] = React.useState<'block' | 'embed'>('block');
	const containerRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		const el = containerRef.current;
		if (!el) {
			return;
		}
		const observer = new ResizeObserver(([entry]) => {
			setAppearance(entry.contentRect.width >= NARROW_EMBED_THRESHOLD ? 'embed' : 'block');
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	// url is always validated at call site (safeUrl); empty string means invalid URL
	if (!url) {
		return null;
	}
	return (
		<div css={nativeEmbedWrapperStyle} ref={containerRef}>
			<Card url={url} appearance={appearance} />
		</div>
	);
};

const i18n = defineMessages({
	editedInParentheses: {
		id: 'comment.renderer.edited.in.parentheses',
		defaultMessage: '(edited)',
		description:
			'This text is shown as a label under the comment content to indicate that a comment has been edited',
	},
	editedBy: {
		id: 'comment.renderer.edited.by',
		defaultMessage: 'Edited by {username} {at}',
		description:
			'This text is shown in the edited label tooltip to display the user who edited the comment and at what time the comment was edited. The {at} will be replaced with a TimeAgo value with preposition enabled.',
	},
});

// Approx Scroll Height for Comment Container with 2 Lines
const MIN_SCROLL_HEIGHT = 50;

const standaloneStyle = css({
	marginTop: token('space.100'),
	paddingBottom: token('space.100'),
});

const commentsPanelV2Style = css({
	paddingBottom: 'unset',
});

const commentEditedLabelStyle = css({
	color: token('color.text.subtlest'),
	font: token('font.body.small'),
	paddingTop: token('space.075'),
});

export const handleIntersection = (
	entry: IntersectionObserverEntry,
	hasBeenViewed: boolean,
	commentId: string,
	setHasBeenViewed: (_viewed: boolean) => void,
	markCommentsRead: any,
): void => {
	if (entry.isIntersecting) {
		// Comment entered the viewport
		setHasBeenViewed(true);
	} else if (hasBeenViewed) {
		// Comment left the viewport after it was viewed
		void markCommentsRead({
			variables: { commentIds: [commentId] },
		});
	}
};

export type CommentRendererProps = {
	commentId: string;
	adf?: DocNode;
	onContentFallback?: (extensionKey?: string) => void;
	isTruncatedContent?: boolean;
	onRendered?: () => void;
	maxHeight?: number;
	fadeOutHeight?: number;
	onComplete?: () => void;
	isInlineComment?: boolean;
	isCommentsPanel?: boolean;
	isEdited?: boolean;
	versionByDisplayName?: string | null;
	versionDate?: string | null;
	commentType?: CommentType;
};

export const CommentRenderer: React.MemoExoticComponent<
	({
		commentId,
		adf,
		onContentFallback,
		isTruncatedContent,
		onRendered,
		maxHeight,
		fadeOutHeight,
		onComplete,
		isInlineComment,
		isCommentsPanel,
		isEdited,
		versionByDisplayName,
		versionDate,
		commentType,
	}: CommentRendererProps) => React.JSX.Element | null
> = memo(
	({
		commentId,
		adf,
		onContentFallback,
		isTruncatedContent,
		onRendered,
		maxHeight,
		fadeOutHeight,
		onComplete,
		isInlineComment,
		isCommentsPanel = false,
		isEdited,
		versionByDisplayName,
		versionDate,
		commentType,
	}: CommentRendererProps): React.JSX.Element | null => {
		const { formatMessage } = useIntl();
		const pageMode = useGetPageMode();
		const [contentId] = usePageContentId();
		const [contentType] = useContentType();
		const spaceId = useSpaceId();
		const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { activationId, cloudId, locale, userId, featureFlags, orgId } = useSessionData();

		const readFragmentInterop = useReadFragmentInterop<any>({
			fragmentSpec: graphql`
				fragment CommentRendererReadFragmentWithAppKey on MacroConnection {
					nodes {
						macroId
						contentId
						renderedMacro(mode: RENDERER) {
							appKey
							value
							macroRenderedRepresentation
							macroBodyStorage
							webResource {
								contexts
								keys
								superbatch {
									metatags
								}
								tags {
									css
									data
									js
								}
								uris {
									css
									data
									js
								}
							}
							mediaToken {
								token
							}
						}
					}
					pageInfo {
						endCursor
						hasNextPage
						__typename
					}
				}
			`,
		});

		let analyticsContextSource = AnalyticsSource.COMMENTS_PANEL;
		let definitionExperienceName: DefinitionExperience = 'commentPanelAutohighlight';
		if (!isCommentsPanel) {
			if (commentType === CommentType.GENERAL) {
				analyticsContextSource = AnalyticsSource.PAGE_FOOTER;
				definitionExperienceName = 'commentFooterAutohighlight';
			} else {
				analyticsContextSource = AnalyticsSource.STANDALONE;
				definitionExperienceName = 'commentInlineAutohighlight';
			}
		}

		const { isRovoEnabled, isRovoLLMEnabled } = useSessionData();
		const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
			contentId,
		});

		const isDefinitionsEnabled =
			(isAIEnabledForContent || isRovoEnabledForContent) &&
			locale.startsWith('en') &&
			!JSON.parse(
				localStorage.getItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT) ||
					'""',
			) &&
			fg('kd_definitions_enable_confluence_comments');

		const cloudIdARI = ConfluenceSiteAri.create({
			siteId: cloudId,
		}).toString();
		const contentARI = contentType
			? getConfluenceContentAri({
					siteId: cloudId,
					resourceType: contentType,
					resourceId: contentId || '',
					activationId: '',
				})
			: '';
		const spaceARI = spaceId
			? getConfluenceContentAri({
					siteId: cloudId,
					resourceType: 'space',
					resourceId: spaceId,
					activationId: '',
				})
			: '';
		const workspaceId = getConfluenceContentAri({
			siteId: cloudId,
			resourceType: 'workspace',
			resourceId: activationId,
			activationId: '',
		});

		// entityAri is empty string to fetch acronyms used in comment text (see loadAutohighlights)
		// rather than acronyms used in associated page to prevent them being highlighted again
		const { keyPhrases, autoHighlightRegex, loadAutohighlights } = useAutohighlightSupplier({
			entityAri: '',
			workspaceId,
			product: 'confluence',
			cloudId,
		});

		const { fetchDefinition } = useDefinitionSupplier({
			workspaceId,
			cloudIdARI,
			product: 'confluence',
			fetchConfig: {
				entity_ari: contentARI,
			},
			productConfig: {
				contentId: contentARI,
				spaceId: spaceARI,
			},
			isDefinitionCurationEnabled: true,
		});

		const onDisableDefinitions = useCallback(() => {
			localStorage.setItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT, true);
		}, []);

		const onEnableDefinitions = useCallback(() => {
			localStorage.setItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT, false);
		}, []);

		const getDefinitionsDisabled = useCallback(() => {
			return localStorage.getItemAsBoolean(
				PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT,
			);
		}, []);

		const definitionRestrictionOptions = useDefinitionRestrictionOptions(contentType);

		useEffect(() => {
			if (fg('kd_definitions_enable_confluence_comments')) {
				const text = adf ? parseContent(adf) : '';
				void loadAutohighlights({
					inputText: {
						text,
						format: KnowledgeDiscoveryKeyPhraseInputTextFormat.PLAIN,
					},
				});
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const memoTextPlatformHighlighter = useMemo(() => {
			const sourceConfig = {
				brand: isRovoEnabled ? AiBrand.ROVO : AiBrand.AI,
				cloudId,
				contentARI,
				contentId,
				experience: definitionExperienceName,
				source: 'readingAids',
				sourceProduct: 'confluence',
				userId,
				workspaceId,
				orgId,
			};

			const curatedDefinitionScopeConfig = {
				workspaceId,
				definitionRestrictionOptions,
				productScopes: {
					contentARI,
					spaceARI,
				},
				cloudId,
				product: 'confluence',
			};

			const definitionConfig = {
				fetchDefinition,
				isAutohighlighted: true,
				definitionCurationOptions: { curatedDefinitionScopeConfig },
				disableDefinitionsCallbacks: {
					onDisableDefinitions,
					onEnableDefinitions,
					getDefinitionsDisabled,
				},
				showChatFollowUps: isRovoLLMEnabled,
			};

			return {
				pattern: autoHighlightRegex,
				component: (props: any) => (
					<RendererDefinitionTextHighlighter
						{...props}
						definitionConfig={definitionConfig}
						keyPhrases={keyPhrases}
						sourceConfig={sourceConfig}
					/>
				),
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [autoHighlightRegex, keyPhrases]);

		const handleContentFallback = (extensionKey?: string) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'fellback',
					actionSubject: 'comment',
					actionSubjectId: commentId,
					source: 'comment',
					attributes: {
						editor: 'v2',
						commentType: 'page',
						reason: extensionKey
							? `${FALLBACK_REASONS.UNSUPPORTED_EXTENSION} - ${extensionKey}`
							: FALLBACK_REASONS.INVALID_ADF,
					},
				},
			}).fire();

			onContentFallback && onContentFallback(extensionKey);
		};

		const extensionHandlers: ExtensionHandlers = useMemo(() => {
			const base = getExtensionHandlers((extensionKey?: string) => {
				if (!extensionKey?.startsWith('native-embed') || !fg('cc_maui_embed_in_comment_editor')) {
					handleContentFallback(extensionKey);
				}
			}, commentId);
			const macroHandlerRaw = base['com.atlassian.confluence.macro.core'];
			const macroHandler: ExtensionHandler =
				typeof macroHandlerRaw === 'function'
					? macroHandlerRaw
					: (extension, context) => {
							const nested = (macroHandlerRaw as unknown as Record<string, ExtensionHandler>)?.[
								extension.extensionKey
							];
							return nested ? nested(extension, context) : null;
						};
			const macroCore: ExtensionHandler = fg('cc_maui_embed_in_comment_editor')
				? (extension, context) => {
						if (extension.extensionKey?.startsWith('native-embed')) {
							const url =
								extension.parameters?.macroMetadata?.url ??
								extension.parameters?.macroParams?.url?.value ??
								'';
							const safeUrl = /^https?:\/\//i.test(url) ? url : '';
							return <NativeEmbedCard url={safeUrl} />;
						}
						return macroHandler(extension, context);
					}
				: macroHandler;
			return { ...base, 'com.atlassian.confluence.macro.core': macroCore };
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [commentId]);
		const { push } = useRouteActions();
		const matchRoute = useMatchRoute();

		const embeddedConfluenceEventHandlers = useEventHandlers();

		const eventHandlers = useMemo(
			() => ({
				...createSmartCardEventHandler({ matchRoute, push }),
				...createLinkEventHandler({ matchRoute, push }),
				...embeddedConfluenceEventHandlers,
			}),
			[matchRoute, push, embeddedConfluenceEventHandlers],
		);

		/* Logic to not show Fade when we have 2 lines in Inactive state. When we have more than 2 lines the scrollHeight is
greater than 50, if its less than 50 send fadeOutHeight as 0*/
		const getFadeHeight = () => {
			if (
				isTruncatedContent &&
				(rootEl?.firstElementChild?.scrollHeight ?? 0) > MIN_SCROLL_HEIGHT
			) {
				return fadeOutHeight;
			} else {
				return 0;
			}
		};

		const injectObjectIdIntoRendererAnalytics = (objectId: string) => (event: UIAnalyticsEvent) => {
			// WS-3190 If a renderer event is fired, inject the commentId as objectId
			if (event.payload.actionSubject === 'renderer') {
				event.update((payload) => ({
					...payload,
					attributes: {
						...payload.attributes,
						appearance: 'comment',
						objectId,
					},
				}));
			}
		};

		const getRendererLocation = useCallback(() => {
			let rendererLocation = isInlineComment
				? CommentRendererLocation.STANDALONE_INLINE
				: CommentRendererLocation.STANDALONE_OBJECT;

			if (isCommentsPanel) {
				if (isInlineComment) {
					rendererLocation = CommentRendererLocation.COMMENTS_PANEL_INLINE;
				} else {
					rendererLocation = CommentRendererLocation.COMMENTS_PANEL_OBJECT;
				}
			}

			return rendererLocation;
		}, [isInlineComment, isCommentsPanel]);

		const handleEmojiLoadFail = useCallback(
			(emojiId?: string, reason?: string) => {
				const rendererLocation = getRendererLocation();

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						objectType: 'page',
						objectId: contentId,
						action: 'failed',
						actionSubject: 'emojiLoad',
						actionSubjectId: 'commentRenderer',
						attributes: {
							commentId,
							location: rendererLocation,
							isLivePage: pageMode === PageMode.LIVE,
							emojiId,
							tenantId: cloudId,
							userId,
							reason,
							pageId: contentId,
						},
					},
				}).fire();
			},
			[pageMode, contentId, commentId, cloudId, userId, getRendererLocation, createAnalyticsEvent],
		);

		const handleEmojiLoadSuccess = useCallback(
			(emojiId?: string) => {
				const rendererLocation = getRendererLocation();

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						objectType: 'page',
						objectId: contentId,
						action: 'success',
						actionSubject: 'emojiLoad',
						actionSubjectId: 'commentRenderer',
						attributes: {
							commentId,
							location: rendererLocation,
							isLivePage: pageMode === PageMode.LIVE,
							emojiId,
							tenantId: cloudId,
							userId,
							pageId: contentId,
						},
					},
				}).fire();
			},
			[pageMode, contentId, commentId, cloudId, userId, getRendererLocation, createAnalyticsEvent],
		);

		if (!adf) {
			return null;
		}

		return (
			<AnalyticsListener
				onEvent={injectObjectIdIntoRendererAnalytics(commentId)}
				channel="editor" // We use the same channel for editor/renderer https://data-portal.internal.atlassian.com/analytics/registry/27447
			>
				<DynamicAnalyticsListener channelParam={editorChannel}>
					<div
						css={[
							!isCommentsPanel && standaloneStyle,
							isCommentsPanel &&
								fg('confluence_frontend_comments_panel_v2') &&
								commentsPanelV2Style,
						]}
						ref={setRootEl}
					>
						<MultiMacroQueryContainer
							cloudId={cloudId}
							contentId={contentId ?? ''}
							featureFlags={featureFlags}
							contentRendererMode="renderer"
							readFragmentInterop={readFragmentInterop}
						>
							<RendererActionsContext>
								<>
									<ADFRenderer
										contentId={commentId}
										document={adf}
										extensionHandlers={extensionHandlers}
										portal={rootEl ?? undefined}
										allowStickyHeaders={false}
										isTruncatedContent={isTruncatedContent}
										onRendered={onRendered}
										onComplete={onComplete}
										allowSelectAllTrap
										maxHeight={maxHeight}
										fadeOutHeight={getFadeHeight()}
										appearance="comment"
										eventHandlers={eventHandlers}
										UNSTABLE_allowTableAlignment={!isInlineComment}
										UNSTABLE_allowTableResizing={!isInlineComment}
										shouldRemoveEmptySpaceAroundContent
										onEmojiLoadFail={handleEmojiLoadFail}
										onEmojiLoadSuccess={handleEmojiLoadSuccess}
										textHighlighter={isDefinitionsEnabled ? memoTextPlatformHighlighter : undefined}
									/>
									{isEdited && (
										<div css={[commentEditedLabelStyle]}>
											<Tooltip
												content={formatMessage(i18n.editedBy, {
													username: versionByDisplayName || formatMessage(commentsi18n.anonymous),
													at: <TimeAgo date={getValidDate(versionDate)} usePrepositions />,
												})}
												analyticsContext={{
													attributes: {
														source: analyticsContextSource,
														commentType,
														name: 'commentEditedBadgeTooltip',
													},
												}}
											>
												{/* eslint-disable-next-line @atlassian/a11y/no-noninteractive-tabindex */}
												<span data-testid="comment-edited-label" tabIndex={0}>
													<CommentEdited>{formatMessage(i18n.editedInParentheses)}</CommentEdited>
												</span>
											</Tooltip>
										</div>
									)}
								</>
							</RendererActionsContext>
						</MultiMacroQueryContainer>
					</div>
				</DynamicAnalyticsListener>
			</AnalyticsListener>
		);
	},
);
CommentRenderer.displayName = 'CommentRenderer';
